import { EFeatureNames, type ICustomerFeatures, type IPlanFeatures } from './Plan'

export const TIERED_LIMITS = [
  EFeatureNames.LeadsLimit,
  EFeatureNames.ContactsUsageLimit,
  EFeatureNames.ContactsPhoneNumbersLimit,
] as const

export type TieredLimit = (typeof TIERED_LIMITS)[number]

// Contacts were untiered before PLG/SLG, so twelve legacy plans still store one value under the bare
// limit name; leads was per-tier from the start and no plan has an untiered leads.limit to fall back
// on. Verified 2026-09-07 — see docs/ENTITLEMENTS.md for the per-plan shapes and the queries.
const HAS_UNTIERED_FALLBACK: Record<TieredLimit, boolean> = {
  [EFeatureNames.LeadsLimit]: false,
  [EFeatureNames.ContactsUsageLimit]: true,
  [EFeatureNames.ContactsPhoneNumbersLimit]: true,
}

export const PER_TIER_KEYS = Object.values(EFeatureNames).filter(name => /\.tier\d+$/.test(name))

export type FeatureValues = Partial<IPlanFeatures & ICustomerFeatures>

const perTierKey = (limit: TieredLimit, tier: number) => `${limit}.tier${tier}` as keyof FeatureValues

const readLimit = (features: FeatureValues, key: keyof FeatureValues) => features[key] as number | undefined

export const resolveTieredLimit = (planFeatures: FeatureValues, tier: number, limit: TieredLimit) => {
  const valueForThisTier = readLimit(planFeatures, perTierKey(limit, tier))

  if (!HAS_UNTIERED_FALLBACK[limit]) return valueForThisTier

  return valueForThisTier ?? readLimit(planFeatures, limit)
}

export const planFeaturesForTier = (planFeatures: FeatureValues, tier: number) => ({
  ...planFeatures,
  ...Object.fromEntries(TIERED_LIMITS.map(limit => [limit, resolveTieredLimit(planFeatures, tier, limit)])),
})

// Not a real quota: the sentinel the PLG/SLG tier-9 rows store for "no ceiling". The product renders
// it as Unlimited, so anything showing a leads limit to a person has to agree.
export const UNLIMITED_LEADS_QUOTA = 500000

export const areLeadsUnlimited = (leadsLimit?: number) => leadsLimit === UNLIMITED_LEADS_QUOTA
